/**
 * AI API — the platform console's and the tenant workspace's calls.
 *
 * CONTRACT SOURCE: the AI contract in the API repository. The paths below are
 * relative to the transport's base URL (`/api/v1`), so `/ai/platform/providers`
 * here is `/api/v1/ai/platform/providers` on the wire.
 *
 * ---------------------------------------------------------------------------
 * THE CREDENTIAL RULE
 * ---------------------------------------------------------------------------
 * `rotateCredential` is the ONLY method that carries a key, and it sends it
 * once. No method reads a credential back, because the API never returns one:
 * `ProviderSummary` carries the credential STATUS (configured, key version,
 * last rotated) and nothing else.
 *
 * ---------------------------------------------------------------------------
 * TWO AXES, TWO NAMESPACES
 * ---------------------------------------------------------------------------
 * The platform console's calls live under `/ai/platform` and are authorized by
 * the deployment's platform grants. The tenant workspace's calls live under
 * `/ai` and act on the tenant the caller's session resolves to — the tenant id
 * is NOT in the path, because the API derives it from the session.
 */
import { http, generateIdempotencyKey } from '@/api/client/http';
import {
  AiUsageResponseSchema,
  CredentialWriteResponseSchema,
  EffectiveAiSettingsResponseSchema,
  PlatformAiSettingsResponseSchema,
  ProviderDetailResponseSchema,
  ProviderListResponseSchema,
  ProviderTestResponseSchema,
  TenantAiAvailabilityListResponseSchema,
  TenantAiAvailabilityResponseSchema,
} from '@/api/schemas/ai-responses';
import type { CredentialWriteResult } from '@/api/schemas/ai-responses';
import type {
  AiUsageSummary,
  CreateProviderRequest,
  EffectiveAiSettings,
  PlatformAiSettings,
  ProviderSummary,
  ProviderTestResult,
  RotateProviderCredentialRequest,
  TenantAiAvailability,
  UpdatePlatformAiSettingsRequest,
  UpdatePlatformTenantAvailabilityRequest,
  UpdateProviderRequest,
  UpdateTenantAiSettingsRequest,
} from '@/api/schemas/ai';

/** Platform-scoped AI administration. */
const PLATFORM_BASE = '/ai/platform';

/** Tenant-scoped AI configuration, resolved from the caller's session. */
const TENANT_BASE = '/ai';

export const aiApi = {
  // -------------------------------------------------------------------------
  // Platform console
  // -------------------------------------------------------------------------

  /** Every provider the deployment knows about. */
  async listPlatformProviders(): Promise<ProviderSummary[]> {
    const response = await http.get(`${PLATFORM_BASE}/providers`, ProviderListResponseSchema);
    return response.data.items;
  },

  /** Registers a new platform provider. The credential is a separate call. */
  async createPlatformProvider(input: CreateProviderRequest): Promise<ProviderSummary> {
    const response = await http.post(`${PLATFORM_BASE}/providers`, ProviderDetailResponseSchema, {
      body: input,
      idempotencyKey: generateIdempotencyKey(),
    });
    return response.data;
  },

  /** One platform provider, including its credential status. */
  async getPlatformProvider(providerId: string): Promise<ProviderSummary> {
    const response = await http.get(
      `${PLATFORM_BASE}/providers/${providerId}`,
      ProviderDetailResponseSchema,
    );
    return response.data;
  },

  /** Updates a platform provider's configuration. */
  async updatePlatformProvider(
    providerId: string,
    input: UpdateProviderRequest,
    version?: number,
  ): Promise<ProviderSummary> {
    const response = await http.patch(
      `${PLATFORM_BASE}/providers/${providerId}`,
      ProviderDetailResponseSchema,
      { body: input, ifMatch: version },
    );
    return response.data;
  },

  /** Replaces a platform provider's credential. Write-only, never returned. */
  async rotatePlatformCredential(
    providerId: string,
    input: RotateProviderCredentialRequest,
  ): Promise<CredentialWriteResult> {
    const response = await http.post(
      `${PLATFORM_BASE}/providers/${providerId}/credential`,
      CredentialWriteResponseSchema,
      { body: input, idempotencyKey: generateIdempotencyKey() },
    );
    return response.data;
  },

  /** Runs a connection test against a platform provider. */
  async testPlatformProvider(providerId: string): Promise<ProviderTestResult> {
    const response = await http.post(
      `${PLATFORM_BASE}/providers/${providerId}/test`,
      ProviderTestResponseSchema,
      { body: {} },
    );
    return response.data;
  },

  /** The deployment's AI settings. */
  async getPlatformSettings(): Promise<PlatformAiSettings> {
    const response = await http.get(`${PLATFORM_BASE}/settings`, PlatformAiSettingsResponseSchema);
    return response.data;
  },

  /** Updates the deployment's AI settings. */
  async updatePlatformSettings(input: UpdatePlatformAiSettingsRequest): Promise<PlatformAiSettings> {
    const response = await http.patch(
      `${PLATFORM_BASE}/settings`,
      PlatformAiSettingsResponseSchema,
      { body: input },
    );
    return response.data;
  },

  /** Every tenant's AI availability. */
  async listPlatformTenantAvailability(): Promise<TenantAiAvailability[]> {
    const response = await http.get(
      `${PLATFORM_BASE}/tenants`,
      TenantAiAvailabilityListResponseSchema,
    );
    return response.data.items;
  },

  /** One tenant's AI availability. */
  async getPlatformTenantAvailability(tenantId: string): Promise<TenantAiAvailability> {
    const response = await http.get(
      `${PLATFORM_BASE}/tenants/${tenantId}/ai-settings`,
      TenantAiAvailabilityResponseSchema,
    );
    return response.data;
  },

  /** Updates one tenant's AI availability and limits. */
  async updatePlatformTenantAvailability(
    tenantId: string,
    input: UpdatePlatformTenantAvailabilityRequest,
  ): Promise<TenantAiAvailability> {
    const response = await http.patch(
      `${PLATFORM_BASE}/tenants/${tenantId}/ai-settings`,
      TenantAiAvailabilityResponseSchema,
      { body: input },
    );
    return response.data;
  },

  // -------------------------------------------------------------------------
  // Tenant workspace
  // -------------------------------------------------------------------------

  /** The tenant's RESOLVED AI configuration. */
  async getEffectiveSettings(): Promise<EffectiveAiSettings> {
    const response = await http.get(`${TENANT_BASE}/settings`, EffectiveAiSettingsResponseSchema);
    return response.data;
  },

  /** Updates the tenant's own AI settings. */
  async updateEffectiveSettings(
    input: UpdateTenantAiSettingsRequest,
  ): Promise<EffectiveAiSettings> {
    const response = await http.patch(
      `${TENANT_BASE}/settings`,
      EffectiveAiSettingsResponseSchema,
      { body: input },
    );
    return response.data;
  },

  /** The tenant's own providers. */
  async listTenantProviders(): Promise<ProviderSummary[]> {
    const response = await http.get(`${TENANT_BASE}/providers`, ProviderListResponseSchema);
    return response.data.items;
  },

  /** Registers a tenant-owned provider. */
  async createTenantProvider(input: CreateProviderRequest): Promise<ProviderSummary> {
    const response = await http.post(`${TENANT_BASE}/providers`, ProviderDetailResponseSchema, {
      body: input,
      idempotencyKey: generateIdempotencyKey(),
    });
    return response.data;
  },

  /** One tenant-owned provider. */
  async getTenantProvider(providerId: string): Promise<ProviderSummary> {
    const response = await http.get(
      `${TENANT_BASE}/providers/${providerId}`,
      ProviderDetailResponseSchema,
    );
    return response.data;
  },

  /** Updates a tenant-owned provider. */
  async updateTenantProvider(
    providerId: string,
    input: UpdateProviderRequest,
    version?: number,
  ): Promise<ProviderSummary> {
    const response = await http.patch(
      `${TENANT_BASE}/providers/${providerId}`,
      ProviderDetailResponseSchema,
      { body: input, ifMatch: version },
    );
    return response.data;
  },

  /** Replaces a tenant-owned provider's credential. Write-only, never returned. */
  async rotateTenantCredential(
    providerId: string,
    input: RotateProviderCredentialRequest,
  ): Promise<CredentialWriteResult> {
    const response = await http.post(
      `${TENANT_BASE}/providers/${providerId}/credential`,
      CredentialWriteResponseSchema,
      { body: input, idempotencyKey: generateIdempotencyKey() },
    );
    return response.data;
  },

  /** Runs a connection test against a tenant-owned provider. */
  async testTenantProvider(providerId: string): Promise<ProviderTestResult> {
    const response = await http.post(
      `${TENANT_BASE}/providers/${providerId}/test`,
      ProviderTestResponseSchema,
      { body: {} },
    );
    return response.data;
  },

  /** The tenant's usage for the current period. */
  async getUsage(): Promise<AiUsageSummary> {
    const response = await http.get(`${TENANT_BASE}/usage`, AiUsageResponseSchema);
    return response.data;
  },
};
